import type { MenuTargetProps } from "@mantine/core";
import { Menu as MantineMenu } from "@mantine/core";
import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useRef,
} from "react";

/**
 * Wrapper around Mantine's Menu.Target that conditionally applies
 * `aria-controls` only when the menu is open. This fixes the
 * `aria_id_unique` WCAG violation where Mantine references a dropdown
 * ID that doesn't exist in the DOM until the menu opens.
 *
 * Since the parent Menu has `withRoles={false}`, Mantine won't set any
 * ARIA attributes automatically. This component re-adds `aria-haspopup`
 * and `aria-expanded` via a MutationObserver, and only adds
 * `aria-controls` when `data-expanded` is present (menu is open).
 */
export function MenuTarget({ children, ...props }: MenuTargetProps) {
  const observerRef = useRef<MutationObserver | null>(null);

  const setRef = useCallback((node: HTMLElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!node) {
      return;
    }

    const applyAriaAttributes = () => {
      const isExpanded = node.hasAttribute("data-expanded");
      node.setAttribute("aria-haspopup", "menu");
      node.setAttribute("aria-expanded", String(isExpanded));

      const targetId = node.getAttribute("id");
      if (isExpanded && targetId) {
        const dropdownId = targetId.replace("-target", "-dropdown");
        node.setAttribute("aria-controls", dropdownId);
      } else {
        node.removeAttribute("aria-controls");
      }
    };

    applyAriaAttributes();

    observerRef.current = new MutationObserver(applyAriaAttributes);
    observerRef.current.observe(node, {
      attributes: true,
      attributeFilter: ["data-expanded"],
    });
  }, []);

  const child = Children.only(children);
  const wrappedChild = isValidElement(child)
    ? cloneElement(child as React.ReactElement<{ ref?: typeof setRef }>, {
        ref: setRef,
      })
    : child;

  return <MantineMenu.Target {...props}>{wrappedChild}</MantineMenu.Target>;
}

MenuTarget.displayName = "@mantine/core/MenuTarget";
